(function () {
  var groupSelector = '.product-recommendation-heading-main';
  var headingSelector = '.related-products__heading, h2.title';
  var buttonSelector = '.product-recommendation-view-all';
  var alignedAttribute = 'data-font-metrics-aligned';
  var baselineAttribute = 'data-recommendation-heading-baseline';
  var buttonBaselineAttribute = 'data-recommendation-button-baseline';
  var textHostClass = 'recommendation-heading-metrics__text';
  var canvas = document.createElement('canvas');
  var context = canvas.getContext && canvas.getContext('2d');
  var pendingFrame = 0;
  var observedHeadings = new WeakSet();
  var resizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(function () {
          scheduleAlignment();
        })
      : null;

  function getCanvasFont(style) {
    return [style.fontStyle, style.fontVariant, style.fontWeight, style.fontSize, style.fontFamily]
      .filter(Boolean)
      .join(' ');
  }

  function ensureTextHost(heading) {
    var children = heading.children;

    if (children.length === 1 && !children[0].hasAttribute(baselineAttribute)) {
      return children[0];
    }

    var existingHost = heading.querySelector('.' + textHostClass);
    if (existingHost) return existingHost;

    var host = document.createElement('span');
    host.className = textHostClass;

    while (heading.firstChild) {
      host.appendChild(heading.firstChild);
    }

    heading.appendChild(host);
    return host;
  }

  function ensureBaselineProbe(host, attribute) {
    var existingProbe = host.querySelector('[' + attribute + ']');
    if (existingProbe) return existingProbe;

    var probe = document.createElement('span');
    probe.setAttribute(attribute, '');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'display:inline-block;width:0;height:0;padding:0;margin:0;border:0;vertical-align:baseline;';
    host.appendChild(probe);
    return probe;
  }

  function measureTextInk(text, style) {
    if (!context || typeof context.measureText !== 'function') return null;

    var font = getCanvasFont(style);
    if (!font) return null;

    context.font = font;
    var textMetrics = context.measureText(text);
    var bounds = {
      ascent: textMetrics.actualBoundingBoxAscent,
      descent: textMetrics.actualBoundingBoxDescent,
    };

    if (!Number.isFinite(bounds.ascent) || !Number.isFinite(bounds.descent)) return null;
    return bounds;
  }

  function measureInkBounds(heading, ignoreYDescender) {
    if (!context || typeof context.measureText !== 'function') return null;

    var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    var ascent = 0;
    var descent = 0;
    var fallbackDescent = 0;
    var foundDescentReference = false;
    var foundText = false;
    var textNode;

    while ((textNode = walker.nextNode())) {
      if (!textNode.data.trim()) continue;

      var parent = textNode.parentElement || heading;
      var style = window.getComputedStyle(parent);
      var metrics = measureTextInk(textNode.data, style);
      if (!metrics) return null;

      ascent = Math.max(ascent, metrics.ascent);
      fallbackDescent = Math.max(fallbackDescent, metrics.descent);

      var descentText = ignoreYDescender ? textNode.data.replace(/[yY]/g, '') : textNode.data;
      if (descentText.trim()) {
        var descentMetrics = ignoreYDescender ? measureTextInk(descentText, style) : metrics;
        if (!descentMetrics) return null;
        descent = Math.max(descent, descentMetrics.descent);
        foundDescentReference = true;
      }
      foundText = true;
    }

    if (!foundDescentReference) descent = fallbackDescent;
    if (!foundText || ascent + descent <= 0) return null;
    return { ascent: ascent, descent: descent };
  }

  function clearAlignment(button) {
    button.removeAttribute(alignedAttribute);
    button.style.removeProperty('--recommendation-heading-ink-height');
    button.style.removeProperty('--recommendation-heading-ink-top');
    button.style.removeProperty('--recommendation-view-all-text-offset');
  }

  function alignGroup(group, sharedInkHeight) {
    var heading = group.querySelector(headingSelector);
    var button = group.querySelector(buttonSelector);
    if (!heading || !button) return;

    if (resizeObserver && !observedHeadings.has(heading)) {
      resizeObserver.observe(heading);
      observedHeadings.add(heading);
    }

    clearAlignment(button);

    var headingTextHost = ensureTextHost(heading);
    var probe = ensureBaselineProbe(headingTextHost, baselineAttribute);
    var baselineRect = probe.getBoundingClientRect();
    var buttonRect = button.getBoundingClientRect();
    var bounds = measureInkBounds(heading, true);

    if (!bounds || group.getBoundingClientRect().width === 0 || baselineRect.width !== 0 || baselineRect.height !== 0) {
      clearAlignment(button);
      return;
    }

    var inkTopViewport = baselineRect.top - bounds.ascent;
    var inkHeight = sharedInkHeight || bounds.ascent + bounds.descent;
    var inkTop = inkTopViewport - buttonRect.top;

    button.style.setProperty('--recommendation-heading-ink-height', inkHeight.toFixed(3) + 'px');
    button.style.setProperty('--recommendation-heading-ink-top', inkTop.toFixed(3) + 'px');
    button.setAttribute(alignedAttribute, '');

    var buttonText = button.querySelector('span');
    if (!buttonText) return;

    var buttonProbe = ensureBaselineProbe(buttonText, buttonBaselineAttribute);
    var buttonTextBounds = measureInkBounds(buttonText);
    if (!buttonTextBounds) return;

    var alignedButtonRect = button.getBoundingClientRect();
    var buttonBaselineRect = buttonProbe.getBoundingClientRect();
    var buttonCenter = (alignedButtonRect.top + alignedButtonRect.bottom) / 2;
    var buttonTextCenter =
      buttonBaselineRect.top + (buttonTextBounds.descent - buttonTextBounds.ascent) / 2;
    var buttonTextOffset = buttonCenter - buttonTextCenter;

    button.style.setProperty('--recommendation-view-all-text-offset', buttonTextOffset.toFixed(3) + 'px');
  }

  function alignAll() {
    pendingFrame = 0;
    var groups = Array.prototype.slice.call(document.querySelectorAll(groupSelector));
    var sharedInkHeight = groups.reduce(function (maximum, group) {
      var heading = group.querySelector(headingSelector);
      if (!heading || group.getBoundingClientRect().width === 0) return maximum;

      var bounds = measureInkBounds(heading, true);
      if (!bounds) return maximum;

      return Math.max(maximum, bounds.ascent + bounds.descent);
    }, 0);

    groups.forEach(function (group) {
      alignGroup(group, sharedInkHeight);
    });
  }

  function scheduleAlignment() {
    if (pendingFrame) return;
    pendingFrame = window.requestAnimationFrame(alignAll);
  }

  function containsRecommendationHeading(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    return node.matches(groupSelector) || Boolean(node.querySelector(groupSelector));
  }

  function initialize() {
    scheduleAlignment();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleAlignment);
      if (typeof document.fonts.addEventListener === 'function') {
        document.fonts.addEventListener('loadingdone', scheduleAlignment);
      }
    }

    window.addEventListener('resize', scheduleAlignment, { passive: true });
    document.addEventListener('shopify:section:load', scheduleAlignment);

    new MutationObserver(function (mutations) {
      for (var mutationIndex = 0; mutationIndex < mutations.length; mutationIndex += 1) {
        var addedNodes = mutations[mutationIndex].addedNodes;
        for (var nodeIndex = 0; nodeIndex < addedNodes.length; nodeIndex += 1) {
          if (containsRecommendationHeading(addedNodes[nodeIndex])) {
            scheduleAlignment();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
